import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import Navbar from './Navbar'
import { events } from '../utils/analytics'
import { PageSEO, softwareAppSchema } from '../utils/seo'

/* ─── Reveal wrapper ─────────────────────────────────────────── */
function Reveal({ children, delay = 0, y = 32 }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.div>
  )
}

/* ─── Data ───────────────────────────────────────────────────── */
const STATS = [
  { icon: '⚡', label: 'Riduzione burocrazia', value: '-70%', color: '#22d3ee' },
  { icon: '📄', label: 'Documenti eliminati',  value: '-90%', color: '#a78bfa' },
  { icon: '⏱️', label: 'Risposta emergenze',   value: '3x',   color: '#f472b6' },
]

const FEATURES = [
  {
    icon: '👥',
    title: 'Gestione Equipaggio',
    desc: 'Organigramma digitale, turni automatizzati e tracciamento certificazioni marittime in un unico posto.',
  },
  {
    icon: '📦',
    title: 'Magazzino Digitale',
    desc: 'Inventario real-time, gestione scorte critiche e ottimizzazione automatica degli approvvigionamenti.',
  },
  {
    icon: '🔧',
    title: 'Manutenzioni Smart',
    desc: 'Pianificazione preventiva basata su ore motore, checklist digitali e storico interventi completo.',
  },
  {
    icon: '📱',
    title: 'Mobile-First',
    desc: 'App nativa iOS e Android. Funziona offline in pieno mare, sincronizza non appena torna la connessione.',
  },
  {
    icon: '📊',
    title: 'Analytics',
    desc: 'Dashboard operativa con KPI di bordo, reportistica automatizzata e trend su costi e performance.',
  },
  {
    icon: '🌐',
    title: 'Compliance Globale',
    desc: 'Conforme a SOLAS, MARPOL, STCW e MLC. Documentazione sempre aggiornata per le ispezioni di porto.',
  },
]

const STACK = [
  'React Native', 'Expo', 'Firebase', 'Zustand', 'Firestore', 'SendGrid',
]

/* ─── Component ──────────────────────────────────────────────── */
export default function CrewBoat() {
  useEffect(() => { window.scrollTo(0, 0) }, [])

  return (
    <>
      <PageSEO
        title="CrewBoat — Gestione Equipaggio Nautico"
        description="CrewBoat è l'app mobile per la gestione di equipaggio, turni e flotte nautiche. Pianificazione, comunicazione e controllo documenti per armatori e charter."
        canonical="/crewboat"
        jsonLd={softwareAppSchema({
          name: 'CrewBoat',
          description: 'SaaS per la gestione di equipaggio, turni e flotte nautiche. Pianificazione, comunicazione e controllo documenti.',
          url: 'https://vmdproject.com/crewboat',
          operatingSystem: 'Web, iOS, Android',
          applicationCategory: 'BusinessApplication',
        })}
      />
      <Navbar />
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={{ background: 'var(--color-bg)', minHeight: '100vh', fontFamily: 'var(--font-display)' }}
      >
        {/* ── Hero ──────────────────────────────────────────── */}
        <section style={{ position: 'relative', overflow: 'hidden', paddingTop: '7rem', paddingBottom: '6rem' }}>

          {/* Ambient glow */}
          <div style={{
            position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)',
            width: '700px', height: '480px',
            background: 'radial-gradient(ellipse at center, rgba(34,211,238,0.12) 0%, transparent 70%)',
            pointerEvents: 'none', zIndex: 0,
          }} />
          {/* Grid overlay */}
          <div style={{
            position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
            backgroundImage: 'linear-gradient(rgba(34,211,238,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.04) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%)',
          }} />

          <div className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>

            {/* Mono label */}
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.78rem', letterSpacing: '0.12em',
                color: '#22d3ee', marginBottom: '1.5rem', textTransform: 'lowercase',
              }}
            >
              // crewboat
            </motion.p>

            {/* Status badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.18 }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2rem' }}
            >
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.3)',
                borderRadius: '100px', padding: '0.3rem 0.9rem',
                fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: '#22d3ee', letterSpacing: '0.08em',
              }}>
                <span style={{
                  width: '6px', height: '6px', borderRadius: '50%', background: '#22d3ee',
                  boxShadow: '0 0 8px #22d3ee',
                  animation: 'pulse-dot 2s ease-in-out infinite',
                }} />
                Beta
              </span>
            </motion.div>

            {/* H1 */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
              style={{
                fontFamily: 'var(--font-display)', fontWeight: 800,
                fontSize: 'clamp(2.2rem, 5.5vw, 4rem)',
                lineHeight: 1.15, color: 'var(--color-text)',
                marginBottom: '1.5rem', letterSpacing: '-0.02em',
              }}
            >
              Gestione equipaggi.{' '}
              <br />
              Semplice,{' '}
              <span style={{
                background: 'linear-gradient(135deg, #22d3ee 0%, #a78bfa 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                digitale
              </span>
              , offline.
            </motion.h1>

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.38 }}
              style={{
                fontSize: 'clamp(1rem, 2vw, 1.18rem)', color: 'var(--color-text-2)',
                maxWidth: '580px', margin: '0 auto 2.5rem', lineHeight: 1.7,
              }}
            >
              L'app completa per captain e crew — turni, manutenzioni, inventario
              e comunicazioni in un'unica piattaforma mobile.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}
            >
              <Link to="/crewboat/pricing" className="btn-primary" onClick={() => events.productCta('crewboat')}>
                Vedi i Piani
              </Link>
              <Link to="/collabora" className="btn-secondary" onClick={events.ctaCollabora}>
                Richiedi accesso
              </Link>
            </motion.div>

          </div>
        </section>

        {/* ── Stats ─────────────────────────────────────────── */}
        <section className="section" style={{ paddingTop: '2rem', paddingBottom: '4rem' }}>
          <div className="container">
            <Reveal>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '1.25rem',
              }}>
                {STATS.map((s, i) => (
                  <Reveal key={s.label} delay={i * 0.1}>
                    <div style={{
                      background: 'rgba(13,13,24,0.8)',
                      border: `1px solid rgba(255,255,255,0.08)`,
                      borderTop: `2px solid ${s.color}`,
                      backdropFilter: 'blur(20px)',
                      borderRadius: '20px',
                      padding: '2rem 1.75rem',
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, height: '60px',
                        background: `radial-gradient(ellipse at 50% 0%, ${s.color}18 0%, transparent 70%)`,
                        pointerEvents: 'none',
                      }} />
                      <div style={{ fontSize: '1.8rem', marginBottom: '0.75rem' }}>{s.icon}</div>
                      <div style={{
                        fontFamily: 'var(--font-display)', fontWeight: 800,
                        fontSize: '2.6rem', color: s.color, lineHeight: 1,
                        letterSpacing: '-0.03em', marginBottom: '0.5rem',
                      }}>
                        {s.value}
                      </div>
                      <div style={{
                        fontFamily: 'var(--font-display)', fontSize: '0.85rem',
                        color: 'var(--color-muted)', letterSpacing: '0.02em',
                      }}>
                        {s.label}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── Features ──────────────────────────────────────── */}
        <section className="section">
          <div className="container">
            <Reveal>
              <p style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.78rem', letterSpacing: '0.12em',
                color: '#22d3ee', marginBottom: '0.75rem', textTransform: 'lowercase',
              }}>
                // funzionalità
              </p>
              <h2 style={{
                fontFamily: 'var(--font-display)', fontWeight: 800,
                fontSize: 'clamp(1.7rem, 3.5vw, 2.6rem)',
                color: 'var(--color-text)', letterSpacing: '-0.02em',
                marginBottom: '0.75rem',
              }}>
                Tutto quello che serve a bordo.
              </h2>
              <p style={{
                color: 'var(--color-muted)', fontSize: '1rem',
                maxWidth: '520px', lineHeight: 1.65, marginBottom: '3.5rem',
              }}>
                Un'unica app sostituisce fogli Excel, gruppi WhatsApp e cartelle cartacee.
                Progettata con e per i professionisti marittimi.
              </p>
            </Reveal>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1.25rem',
            }}>
              {FEATURES.map((f, i) => (
                <Reveal key={f.title} delay={i * 0.08}>
                  <div
                    style={{
                      background: 'rgba(13,13,24,0.8)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderTop: '2px solid rgba(34,211,238,0.35)',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '20px',
                      padding: '2rem',
                      height: '100%',
                      transition: 'border-color 0.25s, transform 0.25s',
                      cursor: 'default',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderTopColor = 'rgba(34,211,238,0.8)'
                      e.currentTarget.style.transform = 'translateY(-3px)'
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderTopColor = 'rgba(34,211,238,0.35)'
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                  >
                    <div style={{
                      fontSize: '1.6rem', marginBottom: '1rem',
                      filter: 'drop-shadow(0 0 8px rgba(34,211,238,0.4))',
                    }}>
                      {f.icon}
                    </div>
                    <h3 style={{
                      fontFamily: 'var(--font-display)', fontWeight: 700,
                      fontSize: '1.05rem', color: 'var(--color-text)',
                      marginBottom: '0.6rem', letterSpacing: '-0.01em',
                    }}>
                      {f.title}
                    </h3>
                    <p style={{
                      color: 'var(--color-muted)', fontSize: '0.9rem', lineHeight: 1.7,
                    }}>
                      {f.desc}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Stack ─────────────────────────────────────────── */}
        <section className="section" style={{ paddingTop: '2rem' }}>
          <div className="container">
            <Reveal>
              <p style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.78rem', letterSpacing: '0.12em',
                color: '#22d3ee', marginBottom: '1.5rem', textTransform: 'lowercase',
              }}>
                // stack tecnologico
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.65rem' }}>
                {STACK.map((tech, i) => (
                  <Reveal key={tech} delay={i * 0.06} y={12}>
                    <span className="stack-badge">{tech}</span>
                  </Reveal>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── CTA finale ────────────────────────────────────── */}
        <section className="section">
          <div className="container">
            <Reveal>
              <div style={{
                background: 'rgba(13,13,24,0.8)',
                border: '1px solid rgba(34,211,238,0.2)',
                backdropFilter: 'blur(20px)',
                borderRadius: '24px',
                padding: 'clamp(2.5rem, 6vw, 4rem)',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Glow center */}
                <div style={{
                  position: 'absolute', bottom: '-30%', left: '50%', transform: 'translateX(-50%)',
                  width: '500px', height: '300px',
                  background: 'radial-gradient(ellipse at center, rgba(34,211,238,0.15) 0%, transparent 70%)',
                  pointerEvents: 'none',
                }} />

                <p style={{
                  fontFamily: 'var(--font-mono)', fontSize: '0.78rem', letterSpacing: '0.12em',
                  color: '#22d3ee', marginBottom: '1.25rem', textTransform: 'lowercase',
                  position: 'relative',
                }}>
                  // pronto a salpare?
                </p>
                <h2 style={{
                  fontFamily: 'var(--font-display)', fontWeight: 800,
                  fontSize: 'clamp(1.9rem, 4vw, 3rem)',
                  color: 'var(--color-text)', letterSpacing: '-0.02em',
                  marginBottom: '1rem', position: 'relative',
                }}>
                  Portalo a{' '}
                  <span style={{
                    background: 'linear-gradient(135deg, #22d3ee 0%, #a78bfa 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}>
                    bordo.
                  </span>
                </h2>
                <p style={{
                  color: 'var(--color-muted)', fontSize: '1rem', lineHeight: 1.7,
                  maxWidth: '480px', margin: '0 auto 2.5rem', position: 'relative',
                }}>
                  Siamo in fase beta e selezioniamo i primi partner. Contattaci per
                  sapere come portare CrewBoat sulla tua imbarcazione.
                </p>

                <div style={{
                  display: 'flex', gap: '1rem', justifyContent: 'center',
                  flexWrap: 'wrap', position: 'relative',
                }}>
                  <Link to="/collabora" className="btn-primary" onClick={() => events.productCta('crewboat')}>
                    Richiedi accesso
                  </Link>
                  <Link to="/crewboat/pricing" className="btn-secondary">
                    Vedi i Piani
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── Footer ────────────────────────────────────────── */}
        <footer style={{
          borderTop: '1px solid rgba(255,255,255,0.06)',
          padding: '2.5rem 0',
        }}>
          <div className="container" style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center',
            justifyContent: 'space-between', gap: '1rem',
          }}>
            <span style={{
              fontFamily: 'var(--font-display)', fontWeight: 700,
              fontSize: '0.9rem', color: 'var(--color-text-2)',
            }}>
              VMDProject
            </span>

            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Link to="/progetti" style={{
                fontFamily: 'var(--font-display)', fontSize: '0.82rem',
                color: 'var(--color-muted)', textDecoration: 'none',
              }}>
                Tutti i progetti
              </Link>
              <Link to="/crewboat/policy" style={{
                fontFamily: 'var(--font-display)', fontSize: '0.82rem',
                color: 'var(--color-muted)', textDecoration: 'none',
                transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = '#22d3ee'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--color-muted)'}
              >
                Privacy
              </Link>
              <Link to="/crewboat/terms" style={{
                fontFamily: 'var(--font-display)', fontSize: '0.82rem',
                color: 'var(--color-muted)', textDecoration: 'none',
                transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = '#22d3ee'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--color-muted)'}
              >
                Termini
              </Link>
            </div>

            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: '0.75rem',
              color: 'var(--color-muted)', letterSpacing: '0.04em',
            }}>
              © 2026
            </span>
          </div>
        </footer>

      </motion.main>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; box-shadow: 0 0 8px #22d3ee; }
          50%       { opacity: 0.5; box-shadow: 0 0 4px #22d3ee; }
        }
      `}</style>
    </>
  )
}
